export const RELOAD_STORAGE_KEY = 'roomsie:preload-error-reload-at'
export const RELOAD_COOLDOWN_MS = 60_000

// The messages browsers use for a dynamic import whose chunk no longer
// exists — the normal result of a tab from before a deploy asking for a
// hash the new build does not have. One pattern per engine:
// WebKit, Chrome, Firefox, webpack-style bundles.
const STALE_CHUNK_PATTERNS = [
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  // Vite preloads a lazy route's CSS alongside its JS, and a stale tab misses
  // the stylesheet for exactly the same reason it misses the chunk. Seen in
  // production as ROOMSIE-REACT-S while the JS variants were already handled —
  // same deploy, same fix, different asset.
  /Unable to preload CSS/i,
]

export const isStaleChunkErrorMessage = message => (
  typeof message === 'string' &&
  STALE_CHUNK_PATTERNS.some(pattern => pattern.test(message))
)

// Recovery for a dynamic import that 404s — the normal shape of a browser
// holding an old bundle after a deploy. Reload once, with a cooldown so a
// genuinely missing chunk cannot spin.
//
// Deliberately NEVER calls event.preventDefault(). Vite's preload helper is:
//
//   function handlePreloadError(err) {
//     const e = new Event('vite:preloadError', { cancelable: true })
//     window.dispatchEvent(e)
//     if (!e.defaultPrevented) throw err        // <-- suppressed by preventDefault
//   }
//   return promise.then(res => baseModule().catch(handlePreloadError))
//
// so preventDefault stops the rethrow, `.catch` returns undefined, and the
// import RESOLVES WITH UNDEFINED. React.lazy then records _status = Resolved
// with _result = undefined and dies on `payload._result.default` — reported
// four times as "Cannot read properties of undefined (reading 'default')"
// (ROOMSIE-E7, REACT-9, REACT-E, REACT-F), each time looking like a mystery
// rather than the stale chunk it was.
//
// Letting the error through costs nothing: the reload is already scheduled,
// and if it does not land first the route error boundary shows a reload
// screen. Either way the failure stays legible.
export function installPreloadErrorRecovery({
  target = window,
  getStorage = () => window.sessionStorage,
  reload = () => window.location.reload(),
  now = () => Date.now(),
} = {}) {
  let reloading = false

  const handlePreloadError = () => {
    if (reloading) return

    const reloadAt = now()

    try {
      const storage = getStorage()
      const previousReloadAt = Number(storage.getItem(RELOAD_STORAGE_KEY))
      if (previousReloadAt && reloadAt - previousReloadAt < RELOAD_COOLDOWN_MS) {
        return
      }

      storage.setItem(RELOAD_STORAGE_KEY, String(reloadAt))
    } catch {
      // Reloading without a record of having done so can loop; let the error
      // boundary handle the failed import instead.
      return
    }

    reloading = true
    reload()
  }

  // Vite dispatches vite:preloadError only from its own __vitePreload helper.
  // A React.lazy import that fails without going through it — the WebKit
  // "Importing a module script failed" shape seen on /host/bookings — arrives
  // solely as an unhandled rejection, so listen for that too. Same reload,
  // same cooldown; and like above, nothing is suppressed: the rejection
  // continues to the error boundary and to Sentry, where the beforeSend
  // filter drops it only when this reload is already underway.
  const handleUnhandledRejection = event => {
    const reason = event?.reason
    const message = (reason && (reason.message || reason.toString?.())) || ''
    if (!isStaleChunkErrorMessage(message)) return
    handlePreloadError()
  }

  target.addEventListener('vite:preloadError', handlePreloadError)
  target.addEventListener('unhandledrejection', handleUnhandledRejection)

  return () => {
    target.removeEventListener('vite:preloadError', handlePreloadError)
    target.removeEventListener('unhandledrejection', handleUnhandledRejection)
  }
}
